import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { port } from '../../App';
import { syncFacebookLeadAPI, passLeadsToCrmAPI } from '../../api-wa/facebookApi';

// 1. Lead Detail View Modal
export const LeadDetailModal = ({ show, onHide, data }) => {
  if (!data) return null;

  const metadata = [
    { label: 'Leadgen ID', value: data.leadgen_id },
    { label: 'Ad Name / Campaign', value: data.ad_name || data.facebook_ad_detail?.ad_name },
    { label: 'Ad ID', value: data.ad_id || data.facebook_ad_detail?.ad_id },
    { label: 'Created At', value: data.created_at ? new Date(data.created_at).toLocaleString() : null },
    { label: 'CRM Sync Status', value: data.crm_sync ? 'SYNCED' : 'PENDING' },
  ];

  const fieldResponses = data.facebook_ad_detail || {};
  const responseKeys = Object.keys(fieldResponses).filter(key => 
    !['ad_id', 'ad_name', 'form_id', 'created_time'].includes(key)
  );

  const formatLabel = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable className="poppins">
      <Modal.Header closeButton className="border-b border-slate-100 pb-3">
        <Modal.Title className="font-bold text-slate-800 text-lg flex items-center gap-2">
          <i className="fab fa-facebook text-blue-600 text-xl"></i> Lead Ad Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3 bg-slate-50/50">
        {/* Metadata Section */}
        <div className="mb-4">
          <h6 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Lead Metadata</h6>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-sm">
            {metadata.map((item, idx) => (
              <div key={idx} className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold text-slate-400 uppercase">{item.label}</span>
                <span className="text-xs text-slate-800 font-semibold break-all">{item.value || '—'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form Field Responses */}
        <div>
          <h6 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Form Field Responses</h6>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white p-4 rounded-2xl border border-slate-200/60 shadow-sm">
            {responseKeys.length === 0 ? (
              <div className="col-span-2 text-center text-xs text-slate-400 py-3 italic">
                No field response data available. Try clicking Re-sync to fetch it.
              </div>
            ) : (
              responseKeys.map((key) => {
                const value = fieldResponses[key];
                return (
                  <div key={key} className="flex flex-col gap-0.5 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{formatLabel(key)}</span>
                    <span className="text-xs text-slate-800 font-semibold break-words">
                      {value !== null && value !== undefined ? String(value) : '—'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-t border-slate-100 pt-3">
        <Button variant="light" onClick={onHide} className="rounded-xl px-4 py-2 text-sm font-semibold border-slate-200 hover:bg-slate-100">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// 2. Sync Lead Button
export const SyncLeadButton = ({ leadgen_id, synced, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleSync = async () => {
    try {
      setLoading(true);
      const res = await syncFacebookLeadAPI({ leadgen_id });
      if (res.success) {
        toast.success(res.message || 'Lead synced successfully');
        if (onSuccess) onSuccess();
      } else {
        toast.error(res.message || 'Sync failed');
      }
    } catch (error) {
      toast.error('An error occurred during sync');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleSync}
      disabled={loading}
      className={`px-3 py-1 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
        loading
          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
          : synced
          ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          : 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
      }`}
    >
      {loading ? 'Syncing...' : synced ? 'Re-sync' : 'Sync Now'}
    </button>
  );
};
